import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import api from '../../utils/api';
import { en } from '../../translations/en';

interface EmbedUrlResponse {
  embedUrl: string;
  dashboardName: string;
}

// Keys mirror the backend allowlist (DASHBOARD_KEYS in admin_analytics.py)
// and the CFN dashboardKeys in analytics-quicksight.ts.
const DASHBOARD_KEYS = [
  'platform-overview',
  'user-activity',
  'content-analytics',
  'business-metrics',
] as const;

type DashboardKey = (typeof DASHBOARD_KEYS)[number];

const AnalyticsTab: React.FC = () => {
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [dashboard, setDashboard] = useState<DashboardKey>('platform-overview');

  const fetchEmbedUrl = useCallback(async (selected: DashboardKey) => {
    setLoading(true);
    setError(null);
    setDisabled(false);

    try {
      const response = await api.get<EmbedUrlResponse>('/api/admin/analytics/embed-url', {
        params: { dashboard: selected },
      });
      setEmbedUrl(response.data.embedUrl);
    } catch (err: unknown) {
      const axiosError = err as { response?: { status: number } };
      if (axiosError?.response?.status === 503) {
        setDisabled(true);
      } else {
        setError(en.admin.analytics.error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmbedUrl(dashboard);
  }, [fetchEmbedUrl, dashboard]);

  if (disabled) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        {en.admin.analytics.disabled}
      </Alert>
    );
  }

  const dashboardTabs = (
    <Tabs
      value={dashboard}
      onChange={(_event, value: DashboardKey) => setDashboard(value)}
      variant="scrollable"
      allowScrollButtonsMobile
      sx={{ borderBottom: 1, borderColor: 'divider' }}
    >
      {DASHBOARD_KEYS.map((key) => (
        <Tab key={key} value={key} label={en.admin.analytics.dashboards[key]} />
      ))}
    </Tabs>
  );

  if (loading) {
    return (
      <Box>
        {dashboardTabs}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        {dashboardTabs}
        <Box sx={{ p: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchEmbedUrl(dashboard)}>
            {en.admin.analytics.refresh}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {dashboardTabs}
      <Box
        component="iframe"
        src={embedUrl ?? undefined}
        title="Analytics Dashboard"
        allow="fullscreen"
        sx={{
          width: '100%',
          height: '750px',
          border: 'none',
          display: 'block',
        }}
      />
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', p: 2 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => fetchEmbedUrl(dashboard)}
        >
          {en.admin.analytics.refresh}
        </Button>
        <Button
          component="a"
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          href="https://us-east-1.quicksight.aws.amazon.com/sn/start"
          target="_blank"
          rel="noopener noreferrer"
        >
          {en.admin.analytics.openInQuickSight}
        </Button>
      </Box>
    </Box>
  );
};

export default AnalyticsTab;
